import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit2, Trash2, Mail, Eye, Info } from "lucide-react";
import { useTemplateSettingsData } from "@/lib/settings/templatesData";
import { emailActionType, smsActionType } from "@/enum/emailActionType";
import { getDefaultTemplates } from "@/dispatchers/templateDispatchers";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseTemplateUpsert } from "@/data/constantStrings";
import {
  showError,
  showSuccess,
} from "@/components/ToastedMessage/ToastedMessage";
import EmailSMSSettingsSkeleton from "./skeletons/EmailSMSSettingsSkeleton";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";

// TypeScript Interfaces
interface Template {
  id: string;
  name: string;
  type: "email" | "sms" | "letter";
  subject?: string;
  content: string;
}

interface EmailSMSLetterTemplate {
  _id: string;
  subject: string;
  text: string;
  createdOn: string;
  messageType: 0 | 1 | 2; // 0 = email, 1 = sms, 2 = letter
  templateName: string;
  companyID: string;
  isDefault: boolean;
  actionType: number;
  isActive: boolean;
}

interface PlaceholderCategory {
  category: string;
  items: PlaceholderItem[];
}

interface PlaceholderItem {
  label: string;
  value: string;
}

interface TemplatesSettingsTabProps {
  templates: EmailSMSLetterTemplate[];
  isDarkMode?: boolean;
  onAddTemplate: (defaultType?: "email" | "sms" | "letter") => void;
  onEditTemplate: (template: EmailSMSLetterTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
}

interface TemplateFormPanelProps {
  template?: EmailSMSLetterTemplate | null;
  defaultType?: "email" | "sms" | "letter";
  isDarkMode?: boolean;
  onSave: (templateData: Template) => void;
  onBack: () => void;
}

interface TemplatePreviewModalProps {
  template: EmailSMSLetterTemplate;
  templateType: string;
  isDarkMode?: boolean;
  onClose: () => void;
}

const TEMPLATE_TYPE_FILTERS = ["all", "email", "sms", "letter"] as const;

const getTemplateTypeLabel = (
  messageType: EmailSMSLetterTemplate["messageType"],
) => (messageType === 0 ? "email" : messageType === 2 ? "letter" : "sms");

const getTemplateBadgeLabel = (
  messageType: EmailSMSLetterTemplate["messageType"],
) => getTemplateTypeLabel(messageType).toUpperCase();

const getTemplateSnippet = (content: string, maxLength = 140) => {
  const normalizedContent = content.replace(/\s+/g, " ").trim();

  if (normalizedContent.length <= maxLength) {
    return normalizedContent;
  }

  return `${normalizedContent.slice(0, maxLength).trimEnd()}...`;
};

// Main Templates Settings Tab Component
export function TemplatesSettingsTab({
  templates,
  onAddTemplate,
  onEditTemplate,
  onDeleteTemplate,
}: TemplatesSettingsTabProps) {
  const { isTemplatesLoading, isDarkMode } = useTemplateSettingsData();
  const [typeFilter, setTypeFilter] = useState<
    "all" | "email" | "sms" | "letter"
  >("all");
  const [
    previewTemplate,
    setPreviewTemplate,
  ] = useState<EmailSMSLetterTemplate | null>(null);

  if (isTemplatesLoading) {
    return <EmailSMSSettingsSkeleton />;
  }

  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );
  const filteredTemplates = (typeFilter === "all"
    ? templates
    : templates.filter(
        (template) => getTemplateTypeLabel(template.messageType) === typeFilter,
      )
  )
    .slice()
    .sort((a, b) => a.templateName.localeCompare(b.templateName));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className={`text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Document Templates
          </h2>
          <p
            className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Manage your email, SMS, and letter templates with dynamic
            placeholders
          </p>
        </div>
        <Button
          onClick={() => {
            if (canUpsertTemplate) {
              onAddTemplate(typeFilter === "all" ? undefined : typeFilter);
            } else {
              setShowPermissionMessage("Create or update template");
              setShowPermissionModal(true);
            }
          }}
          className={`bg-slate-900 hover:bg-slate-800 ${
            !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TEMPLATE_TYPE_FILTERS.map((filter) => (
          <button
            key={filter}
            onClick={() => setTypeFilter(filter)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              typeFilter === filter
                ? isDarkMode
                  ? "bg-white text-gray-900"
                  : "bg-slate-900 text-white"
                : isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            {filter === "all"
              ? "All"
              : filter === "email"
              ? "Email"
              : filter === "letter"
              ? "Letter"
              : filter.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredTemplates.map((template) => (
          <div
            key={template?._id}
            className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3
                    className={`font-medium transition-colors duration-300 break-all ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {template?.templateName}
                  </h3>
                  <Badge
                    variant={
                      template.messageType === 0
                        ? "default"
                        : template.messageType === 2
                        ? "outline"
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {getTemplateBadgeLabel(template.messageType)}
                  </Badge>
                  <Badge
                    variant={template.isActive ? "default" : "secondary"}
                    className="text-xs"
                  >
                    {template?.isActive ? "ACTIVE" : "INACTIVE"}
                  </Badge>
                </div>

                {template.subject && (
                  <p
                    className={`text-sm mb-2 transition-colors duration-300 break-all ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-medium">Subject:</span>{" "}
                    {template.subject}
                  </p>
                )}

                <p
                  className={`text-sm transition-colors duration-300 break-all ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {getTemplateSnippet(template.text)}
                </p>
              </div>

              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewTemplate(template)}
                  className={`${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditTemplate(template)}
                  className={`${
                    isDarkMode
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (canUpsertTemplate) {
                      onDeleteTemplate(template._id);
                    } else {
                      setShowPermissionMessage("Create or update template");
                      setShowPermissionModal(true);
                    }
                  }}
                  className={`${
                    isDarkMode
                      ? "text-red-400 hover:text-red-300"
                      : "text-red-600 hover:text-red-800"
                  } ${
                    !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div
            className={`text-center py-12 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-900"
            }`}
          >
            <Mail
              className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <h3
              className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              No templates yet
            </h3>
            <p
              className={`mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Create your first email or SMS template to get started
            </p>
            <Button
              onClick={onAddTemplate}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Template
            </Button>
          </div>
        )}

        {templates.length > 0 && filteredTemplates.length === 0 && (
          <div
            className={`text-center py-12 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-900"
            }`}
          >
            <Mail
              className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <h3
              className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              No {typeFilter === "all" ? "" : typeFilter} templates found
            </h3>
            <p
              className={`transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Try another filter or create a new template in this category.
            </p>
          </div>
        )}
      </div>

      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          templateType={getTemplateTypeLabel(previewTemplate.messageType)}
          isDarkMode={isDarkMode}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
}

// Template Form Panel Component
export function TemplateFormPanel({
  template,
  onSave,
  onBack,
  defaultType,
}: TemplateFormPanelProps) {
  const {
    updateTemplate,
    addTemplate,
    isDarkMode,
    currentUser,
  } = useTemplateSettingsData();
  const { companyId } = useTemplateSettingsData();
  const [name, setName] = useState("");
  const [type, setType] = useState<"email" | "sms" | "letter">(
    defaultType || "email",
  );
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [templateType, setTemplateType] = useState();
  const [isActive, setIsActive] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);
  const templateTypeArray = Object.entries(emailActionType).map(
    ([id, name]) => ({
      id: Number(id),
      name,
    }),
  );

  const smsTypeArray = Object.entries(smsActionType).map(([id, name]) => ({
    id: Number(id),
    name,
  }));

  const placeholders: PlaceholderCategory[] = [
    {
      category: "Patient",
      items: [
        { label: "Title", value: "{{patient.title}}" },
        { label: "First name", value: "{{patient.firstName}}" },
        { label: "Last name", value: "{{patient.lastName}}" },
        { label: "Address", value: "{{patient.address}}" },
        { label: "DOB", value: "{{patient.dob}}" },
        { label: "Patient email", value: "{{patient.email}}" },
        { label: "Patient phone", value: "{{patient.phone}}" },
      ],
    },
    {
      category: "Appointment",
      items: [
        { label: "Date", value: "{{appointment.date}}" },
        { label: "Time", value: "{{appointment.time}}" },
        { label: "Duration", value: "{{appointment.duration}}" },
        { label: "Service", value: "{{appointment.service}}" },
      ],
    },
    {
      category: "Practice",
      items: [
        { label: "Practice name", value: "{{practice.name}}" },
        { label: "Practice email", value: "{{practice.email}}" },
        { label: "Practice phone", value: "{{practice.phone}}" },
      ],
    },
    {
      category: "Location",
      items: [
        { label: "Location name", value: "{{location.name}}" },
        { label: "Location address", value: "{{location.address}}" },
      ],
    },
    {
      category: "Prescriber",
      items: [{ label: "Prescriber name", value: "{{prescriber.name}}" }],
    },
    // {
    //   category: "Links",
    //   items: [
    //     { label: "Hyperlink", value: "{{link.reschedule}}" },
    //     { label: "Cancel link", value: "{{link.cancel}}" },
    //   ],
    // },
  ];

  const insertPlaceholder = (placeholder: string) => {
    const newContent =
      content.slice(0, cursorPosition) +
      placeholder +
      content.slice(cursorPosition);
    setContent(newContent);
    setCursorPosition(cursorPosition + placeholder.length);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const handleChange = (event) => {
    setTemplateType(event.target.value);

    if (type === "email") {
      getDefaultTemplates(event.target.value);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field === "isActive") {
      setIsActive(value);
    }
    // handle other fields if needed
  };

  const handleSave = async () => {
    if (
      !name.trim() ||
      !content.trim() ||
      ((type === "email" || type === "letter") && !subject.trim()) ||
      (type !== "letter" && !templateType)
    )
      return;

    const templateData = {
      ...template,
      templateName: name.trim(),
      actionType: type !== "letter" ? Number(templateType) : 0, // Set default actionType for letters
      subject: type === "email" || type === "letter" ? subject.trim() : "",
      text: content.trim(),
      isActive,
      messageType: type === "email" ? 0 : type === "sms" ? 1 : 2,
      isDefault: template?.isDefault ?? false,
      createdOn: template?.createdOn || new Date().toISOString(),
      companyID: companyId,
      _id: template?._id,
    };

    try {
      if (template?._id) {
        await updateTemplate(templateData);
        showSuccess("Template updated successfully!");
      } else {
        await addTemplate(templateData); // 409 will throw, going straight to catch
        showSuccess("Template created successfully!");

        const eventName =
          type === "email"
            ? "Email_template_created_first_time"
            : "SMS_template_created_first_time";

        trackFirstTimeIntercomEvent({
          eventName: eventName,
          user: {
            id: currentUser.emailAddress,
            email: currentUser.emailAddress,
            name: currentUser.fullName,
          },
          properties: {
            source: "templates_settings",
          },
        });
      }
      onSave(templateData);
    } catch (err) {
      if (err?.status === 409 || err?.code === "DUPLICATE_TEMPLATE_NAME") {
        setNameError("A template with this name already exists.");
        return;
      }
      showError("Something went wrong while saving template!");
    }
  };

  useEffect(() => {
    if (template) {
      setName(template.templateName || "");
      setType(
        template.messageType === 0
          ? "email"
          : template.messageType === 2
          ? "letter"
          : "sms",
      );
      setSubject(template.subject || "");
      setContent(template.text || "");
      // Only set templateType for non-letter templates
      if (template.messageType !== 2) {
        setTemplateType(template.actionType?.toString() || "");
      }
      setIsActive(template?.isActive);
      setNameError(null);
    }
  }, [template]);

  return (
    <div className="flex h-full">
      {/* Main Content Area */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Button
                variant="ghost"
                onClick={onBack}
                className={`mb-2 -ml-2 ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : ""
                }`}
              >
                {"<- Back to Templates"}
              </Button>
              <h2
                className={`text-xl font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {template ? "Edit Template" : "Create New Template"}
              </h2>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Template Name */}
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter template name"
                className="max-w-md"
                maxLength={100}
              />
              <CharLimitMessage value={name} max={100} />
              {nameError && (
                <p className="text-xs text-red-500 mt-1">{nameError}</p>
              )}
              <p className="text-xs text-gray-500">
                Character count: {name?.length}
              </p>
            </div>

            {/* Template Type */}
            <div className="space-y-2">
              <Label>Message Type *</Label>
              <Select
                value={type}
                onValueChange={(value) =>
                  setType(value as "email" | "sms" | "letter")
                }
              >
                <SelectTrigger className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="letter">Letter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Template Type - Hide for letters */}
            {type !== "letter" && (
              <div className="space-y-2">
                <Label>Template Type *</Label>
                <Select
                  value={templateType}
                  onValueChange={(value) => handleChange({ target: { value } })}
                >
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select template type" />
                  </SelectTrigger>
                  <SelectContent>
                    {type === "email"
                      ? templateTypeArray.map((template) => (
                          <SelectItem
                            key={template.id}
                            value={template.id.toString()}
                          >
                            {template?.name}
                          </SelectItem>
                        ))
                      : smsTypeArray.map((template) => (
                          <SelectItem
                            key={template.id}
                            value={template.id.toString()}
                          >
                            {template?.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Email Subject (only for email templates) */}
            {(type === "email" || type === "letter") && (
              <div className="space-y-2">
                <Label>
                  {type === "letter"
                    ? "Letter Subject / Reference *"
                    : "Email Subject *"}
                </Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={
                    type === "letter"
                      ? "Enter letter subject or reference"
                      : "Enter email subject"
                  }
                  maxLength={150}
                />
                <CharLimitMessage value={subject} max={150} />
                <p className="text-xs text-gray-500">
                  Character count: {subject?.length}
                </p>
              </div>
            )}

            {type !== "letter" && (
              <div className="flex items-center mt-4 space-x-2">
                <Switch
                  id="team-active"
                  checked={isActive}
                  onCheckedChange={(checked) =>
                    handleInputChange("isActive", checked)
                  }
                />
                <Label htmlFor="team-active">Active</Label>
              </div>
            )}

            {/* Message Content */}
            <div className="space-y-2">
              <Label>
                {type === "email"
                  ? "Email Message Content"
                  : type === "letter"
                  ? "Letter Content"
                  : "SMS Message Content"}{" "}
                *
              </Label>
              <Textarea
                value={content}
                maxLength={
                  type === "email" ? 2000 : type === "letter" ? 5000 : 500
                }
                onChange={handleContentChange}
                onSelect={(e) =>
                  setCursorPosition(
                    (e.target as HTMLTextAreaElement).selectionStart,
                  )
                }
                placeholder={`Enter your ${
                  type === "letter" ? "letter" : type
                } content here. Use the placeholders from the sidebar to insert dynamic content.`}
                className="min-h-[300px] max-h-[500px] font-mono text-sm"
              />
              <CharLimitMessage
                value={content}
                max={type === "email" ? 2000 : type === "letter" ? 5000 : 500}
              />
              {type === "sms" ? (
                <div className="mt-1">
                  <div
                    className={`flex items-start gap-1 text-[11px] ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <Info className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>
                      SMS messages are billed at 160 characters each. Actual
                      message length can vary depending on the placeholder
                      values used.
                    </span>
                  </div>
                </div>
              ) : (
                <p
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {type === "letter"
                    ? `Character count: ${content.length}`
                    : `Character count: ${content.length}/2000`}
                </p>
              )}
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <Button
                onClick={() => {
                  if (checkUserPermissions(accessTitanverseTemplateUpsert)) {
                    handleSave();
                  } else {
                    setShowPermissionMessage("Create or update template");
                    setShowPermissionModal(true);
                  }
                }}
                className={`bg-slate-900 hover:bg-slate-800 ${
                  !checkUserPermissions(accessTitanverseTemplateUpsert)
                    ? "opacity-50 cursor-not-allowed hover:bg-slate-900"
                    : ""
                }`}
                disabled={
                  !name.trim() ||
                  !content.trim() ||
                  ((type === "email" || type === "letter") &&
                    !subject.trim()) ||
                  (type !== "letter" && !templateType)
                  // ||
                  // !checkUserPermissions(accessTitanverseTemplateUpsert)
                }
              >
                {template ? "Update Template" : "Create Template"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Placeholders Sidebar */}
      <div
        className={`w-80 border-l p-4 overflow-y-auto transition-colors duration-300 ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <div
          className={`sticky top-0 pb-4 transition-colors duration-300 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-50"
          }`}
        >
          <h3
            className={`font-semibold mb-2 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Placeholders
          </h3>
          <p
            className={`text-xs mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            You can click on any of these tags to include them in your content.
          </p>
        </div>

        <div className="space-y-4">
          {placeholders.map((category) => (
            <div key={category.category}>
              <h4
                className={`font-medium mb-2 text-sm transition-colors duration-300 ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {category.category}
              </h4>
              <div className="space-y-1">
                {category.items.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => insertPlaceholder(item.value)}
                    className="w-full px-3 py-2 text-sm text-left text-white transition-colors bg-teal-600 rounded hover:bg-teal-700"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Preview Section */}
        <div
          className={`mt-6 pt-4 border-t transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-300"
          }`}
        >
          <h4
            className={`font-medium mb-2 text-sm transition-colors duration-300 ${
              isDarkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            Preview
          </h4>
          <div
            className={`rounded p-3 text-xs border transition-colors duration-300 break-words ${
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            {type === "email" && subject && (
              <div className="mb-2">
                <strong>Subject:</strong> {subject}
              </div>
            )}
            <div
              className={`whitespace-pre-wrap transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {content || "Your content will appear here..."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Template Preview Modal Componen
export function TemplatePreviewModal({
  template,
  templateType,
  isDarkMode = false,
  onClose,
}: TemplatePreviewModalProps) {
  // Sample data for template preview
  const sampleData = {
    patient: {
      title: "Mr.",
      firstName: "David",
      lastName: "James",
      address: "42 Queen Square, Bristol BS1 4QP",
      dob: "14 February 1988",
      email: "david.james@email.com",
      phone: "+44 (0) 777 649 55 86",
    },
    appointment: {
      date: "Monday, 15th January 2024",
      time: "10:30 AM",
      duration: "30 minutes",
      service: "Blood Pressure Check",
    },
    practice: {
      name: "TITAN Pharmacy",
      email: "appointments@lipspharmacy.co.uk",
      phone: "+44 (0) 117 456 7890",
    },
    location: {
      name: "TITAN Pharmacy",
      address: "123 Bedminster St, Bristol BS3 4AB",
    },
    prescriber: {
      name: "Dr. Sarah Williams",
      title: "Dr.",
    },
    link: {
      reschedule: "https://lipspharmacy.co.uk/reschedule/abc123",
      cancel: "https://lipspharmacy.co.uk/cancel/abc123",
    },
  };

  // Function to replace placeholders with sample data
  const processTemplate = (content: string): string => {
    let processedContent = content;

    // Replace patient placeholders
    processedContent = processedContent.replace(
      /\{\{patient\.title\}\}/g,
      sampleData.patient.title,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.firstName\}\}/g,
      sampleData.patient.firstName,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.lastName\}\}/g,
      sampleData.patient.lastName,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.address\}\}/g,
      sampleData.patient.address,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.dob\}\}/g,
      sampleData.patient.dob,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.email\}\}/g,
      sampleData.patient.email,
    );
    processedContent = processedContent.replace(
      /\{\{patient\.phone\}\}/g,
      sampleData.patient.phone,
    );

    // Replace appointment placeholders
    processedContent = processedContent.replace(
      /\{\{appointment\.date\}\}/g,
      sampleData.appointment.date,
    );
    processedContent = processedContent.replace(
      /\{\{appointment\.time\}\}/g,
      sampleData.appointment.time,
    );
    processedContent = processedContent.replace(
      /\{\{appointment\.duration\}\}/g,
      sampleData.appointment.duration,
    );
    processedContent = processedContent.replace(
      /\{\{appointment\.service\}\}/g,
      sampleData.appointment.service,
    );

    // Replace practice placeholders
    processedContent = processedContent.replace(
      /\{\{practice\.name\}\}/g,
      sampleData.practice.name,
    );
    processedContent = processedContent.replace(
      /\{\{practice\.email\}\}/g,
      sampleData.practice.email,
    );
    processedContent = processedContent.replace(
      /\{\{practice\.phone\}\}/g,
      sampleData.practice.phone,
    );

    // Replace location placeholders
    processedContent = processedContent.replace(
      /\{\{location\.name\}\}/g,
      sampleData.location.name,
    );
    processedContent = processedContent.replace(
      /\{\{location\.address\}\}/g,
      sampleData.location.address,
    );

    processedContent = processedContent.replace(
      /\{\{prescriber\.name\}\}/g,
      sampleData.prescriber.name,
    );
    processedContent = processedContent.replace(
      /\{\{prescriber\.title\}\}/g,
      sampleData.prescriber.title,
    );

    // Replace link placeholders
    processedContent = processedContent.replace(
      /\{\{link\.reschedule\}\}/g,
      sampleData.link.reschedule,
    );
    processedContent = processedContent.replace(
      /\{\{link\.cancel\}\}/g,
      sampleData.link.cancel,
    );

    return processedContent;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-lg p-6 shadow-xl ${
          isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Template Preview</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="font-medium">{template.templateName}</span>
            <Badge variant="outline">{templateType.toUpperCase()}</Badge>
            <Badge variant={template.isActive ? "default" : "secondary"}>
              {template.isActive ? "ACTIVE" : "INACTIVE"}
            </Badge>
          </div>
          {template.subject && (
            <div>
              <Label className="text-sm font-medium">
                {templateType === "letter" ? "Subject/Reference:" : "Subject:"}
              </Label>
              <p
                className={`p-2 text-sm border rounded ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                {processTemplate(template.subject)}
              </p>
            </div>
          )}
          <div>
            <Label className="text-sm font-medium">Content:</Label>
            <div
              className={`p-4 text-sm whitespace-pre-wrap border rounded ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-200"
                  : "bg-gray-50 border-gray-200 text-gray-700"
              }`}
            >
              {processTemplate(template.text)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
